import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BackLink, Container, Header, Subtitle, Title } from '../admin/ui';
import EmailTemplates from './EmailTemplates';
import EmailTemplateEditor from './EmailTemplateEditor';
import EmailSchedules from './EmailSchedules';
import EmailUpcoming from './EmailUpcoming';
import SentEmails from './SentEmails';

// The admin E-Mails screen: the templates ("Emails", the first tab), the log of what was sent
// ("Sent"), the automatic emails' schedules ("Schedules") and what they would send in the next days
// ("Upcoming"). The tab lives in the URL (?tab=emails|sent|schedules|upcoming) so a link can land on
// any of them; the template editor is a deep link of its own (?tab=emails&key=PASSWORD_RESET) and a
// schedule card is addressed by a hash (?tab=schedules#schedule-EVENT_REMINDER). The Sent tab's
// filters stay in the component and never reach the URL.
const TABS = [
  { id: 'emails', labelId: 'emailAdmin.tab.emails', defaultMessage: 'Emails' },
  { id: 'sent', labelId: 'emailAdmin.tab.sent', defaultMessage: 'Sent' },
  { id: 'schedules', labelId: 'emailAdmin.tab.schedules', defaultMessage: 'Schedules' },
  { id: 'upcoming', labelId: 'emailAdmin.tab.upcoming', defaultMessage: 'Upcoming' },
];
const TAB_IDS = TABS.map(({ id }) => id);

// The four tabs fit one row on a phone with the narrower padding; a longer label wraps the bar onto
// a second row instead of pushing the page sideways, and a label never breaks inside ("E-Mails").
const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .25rem;
  border-bottom: 1px solid #dbe3ea;
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  border: 0;
  border-bottom: 3px solid ${(p) => (p.$active ? '#47ABDB' : 'transparent')};
  padding: .6rem .9rem;
  background: transparent;
  color: ${(p) => (p.$active ? '#111D4E' : '#64748b')};
  font: inherit;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #111D4E;
  }

  @media (max-width: 480px) {
    padding: .55rem .5rem;
  }
`;

const EmailAdminScreen = () => {
  const intl = useIntl();
  const [params, setParams] = useSearchParams();
  const tab = TAB_IDS.includes(params.get('tab')) ? params.get('tab') : 'emails';
  const templateKey = tab === 'emails' ? params.get('key') : null;

  // The editor and the schedule cards report whether they hold unsaved text/settings, so a tab
  // switch, the dashboard link, any in-app link and the browser's own way out can ask first.
  const [editorDirty, setEditorDirty] = useState(false);
  const [schedulesDirty, setSchedulesDirty] = useState(false);
  const [sentType, setSentType] = useState('');
  const dirty = editorDirty || schedulesDirty;

  const confirmLeave = () => !dirty
    || window.confirm(intl.formatMessage({
      id: 'emailAdmin.template.leaveConfirm',
      defaultMessage: 'You have unsaved changes. Leave and lose them?',
    }));

  // Reload, close, a typed URL.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Every in-app link (the header's, the sidebar's, the dashboard link) while anything is unsaved.
  useEffect(() => {
    if (!dirty) return undefined;
    const message = intl.formatMessage({
      id: 'emailAdmin.template.leaveConfirm',
      defaultMessage: 'You have unsaved changes. Leave and lose them?',
    });
    const guard = (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (!window.confirm(message)) event.preventDefault();
    };
    document.addEventListener('click', guard, true);
    return () => document.removeEventListener('click', guard, true);
  }, [dirty, intl]);

  const selectTab = (next) => {
    if (next === tab) return;
    if (!confirmLeave()) return;
    setEditorDirty(false);
    setSchedulesDirty(false);
    setSentType('');
    setParams({ tab: next });
  };

  const openTemplate = (key) => setParams({ tab: 'emails', key });
  const closeEditor = () => setParams({ tab: 'emails' });
  const showSent = (mailType) => {
    setEditorDirty(false);
    setSentType(mailType || '');
    setParams({ tab: 'sent' });
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard">
        <FormattedMessage id="emailAdmin.back" defaultMessage="Back to the dashboard" />
      </BackLink>
      <Header>
        <div>
          <Title><FormattedMessage id="emailAdmin.title" defaultMessage="E-Mails" /></Title>
          <Subtitle>
            <FormattedMessage id="emailAdmin.subtitle"
              defaultMessage="Every email the system sends: edit its template, set when the automatic ones go out, and see what was sent." />
          </Subtitle>
        </div>
      </Header>

      <TabBar role="tablist" aria-label={intl.formatMessage({ id: 'emailAdmin.tabs.label', defaultMessage: 'E-Mail views' })}>
        {TABS.map(({ id, labelId, defaultMessage }) => (
          <Tab key={id} type="button" role="tab" id={`email-tab-${id}`} aria-selected={tab === id}
            aria-controls="email-tabpanel" $active={tab === id} onClick={() => selectTab(id)}>
            <FormattedMessage id={labelId} defaultMessage={defaultMessage} />
          </Tab>
        ))}
      </TabBar>

      <div role="tabpanel" id="email-tabpanel" aria-labelledby={`email-tab-${tab}`}>
        {tab === 'emails' && (templateKey
          ? (
            <EmailTemplateEditor key={templateKey} templateKey={templateKey}
              onBack={closeEditor} onDirtyChange={setEditorDirty} onShowSent={showSent} />
          )
          : <EmailTemplates onOpen={openTemplate} />)}
        {tab === 'sent' && <SentEmails key={sentType || 'all'} initialType={sentType} />}
        {tab === 'schedules' && <EmailSchedules onDirtyChange={setSchedulesDirty} />}
        {tab === 'upcoming' && <EmailUpcoming />}
      </div>
    </Container>
  );
};

export default EmailAdminScreen;
